import { useState } from 'react';
import {
  CogIcon,
  EnvelopeIcon,
  UserGroupIcon,
  UsersIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  PhoneIcon,
  ChartBarIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  ComputerDesktopIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { SettingsProvider } from '../../../contexts/SettingsContext';
import GoogleAnalyticsForm from '../../../components/settings/GoogleAnalyticsForm';
import MobileUserManagement from '../../components/MobileUserManagement';
import CommunicationSettingsForm from '../../../components/settings/CommunicationSettingsForm';
import MobileSettingsLegal from './settings/MobileSettingsLegal';
import MobileSettingsEmail from './settings/MobileSettingsEmail';
import MobileSettingsUserRoles from './settings/MobileSettingsUserRoles';
import MobileSettingsContactBanking from './settings/MobileSettingsContactBanking';
import MobileSettingsRegistration from './settings/MobileSettingsRegistration';
import MobileSettingsSystem from './settings/MobileSettingsSystem';
import MobilePreviewEnvironments from '../../components/MobilePreviewEnvironments';
import MobileSettingsAccount from './settings/MobileSettingsAccount';

type SettingsTab = 'menu' | 'system' | 'registration' | 'contact' | 'roles' | 'users' | 'communications' | 'email' | 'legal' | 'analytics' | 'preview' | 'account';

interface SettingsItem {
  key: SettingsTab;
  label: string;
  description: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  color: string;
}

const SETTINGS_ITEMS: SettingsItem[] = [
  { key: 'system', label: 'System Settings', description: 'General tournament configuration', icon: CogIcon, color: 'bg-gray-50 text-gray-600' },
  { key: 'registration', label: 'Registration', description: 'Registration options and limits', icon: ClipboardDocumentListIcon, color: 'bg-golf-orange/10 text-golf-orange' },
  { key: 'contact', label: 'Contact & Banking', description: 'Contact info and bank details', icon: PhoneIcon, color: 'bg-blue-50 text-blue-600' },
  { key: 'roles', label: 'User Roles', description: 'Manage admin access', icon: UserGroupIcon, color: 'bg-teal-50 text-teal-600' },
  { key: 'users', label: 'Users', description: 'Manage user accounts', icon: UsersIcon, color: 'bg-cyan-50 text-cyan-600' },
  { key: 'communications', label: 'Communications', description: 'Notification and messaging settings', icon: EnvelopeIcon, color: 'bg-amber-50 text-amber-600' },
  { key: 'email', label: 'Email Settings', description: 'Email provider configuration', icon: EnvelopeIcon, color: 'bg-rose-50 text-rose-600' },
  { key: 'legal', label: 'Legal Documents', description: 'Terms, privacy policy', icon: DocumentTextIcon, color: 'bg-slate-50 text-slate-600' },
  { key: 'analytics', label: 'Google Analytics', description: 'Analytics tracking setup', icon: ChartBarIcon, color: 'bg-green-50 text-green-600' },
  { key: 'preview', label: 'Preview', description: 'Preview site across devices', icon: ComputerDesktopIcon, color: 'bg-sky-50 text-sky-600' },
  { key: 'account', label: 'My Account', description: 'Manage or close your account', icon: UserCircleIcon, color: 'bg-red-50 text-red-500' },
];

function SettingsContent({ tab }: { tab: SettingsTab }) {
  switch (tab) {
    case 'system': return <MobileSettingsSystem />;
    case 'registration': return <MobileSettingsRegistration />;
    case 'contact': return <MobileSettingsContactBanking />;
    case 'roles': return <MobileSettingsUserRoles />;
    case 'users': return <MobileUserManagement />;
    case 'communications': return <CommunicationSettingsForm />;
    case 'email': return <MobileSettingsEmail />;
    case 'legal': return <MobileSettingsLegal />;
    case 'analytics': return <GoogleAnalyticsForm />;
    case 'preview': return <MobilePreviewEnvironments />;
    case 'account': return <MobileSettingsAccount />;
    default: return null;
  }
}

export default function MobileAdminSettings() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('menu');

  const visibleItems = SETTINGS_ITEMS;

  const activeItem = visibleItems.find(i => i.key === activeTab);

  return (
    <SettingsProvider>
      {activeTab === 'menu' ? (
        <div className="px-4 py-4 space-y-2">
          {visibleItems.map(item => (
            <button
              key={item.key}
              onClick={() => setActiveTab(item.key)}
              className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3 text-left active:bg-gray-50 transition-colors"
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${item.color}`}>
                <item.icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-golf-gray text-sm">{item.label}</p>
                <p className="text-xs text-gray-400 truncate">{item.description}</p>
              </div>
              <ChevronRightIcon className="w-4 h-4 text-gray-300 flex-shrink-0" />
            </button>
          ))}
        </div>
      ) : (
        <div className="px-4 py-4">
          <button
            onClick={() => setActiveTab('menu')}
            className="flex items-center gap-1.5 text-golf-orange text-sm font-medium mb-4"
          >
            <ChevronLeftIcon className="w-4 h-4" />
            Settings
          </button>
          <div className="mb-4">
            <h2 className="text-base font-bold text-golf-gray">{activeItem?.label}</h2>
            <p className="text-xs text-gray-500">{activeItem?.description}</p>
          </div>
          <SettingsContent tab={activeTab} />
        </div>
      )}
    </SettingsProvider>
  );
}
